import { Injectable, inject } from '@angular/core';
import { type NavigationExtras, Router } from '@angular/router';
import { createEffect } from 'ngxtension/create-effect';
import { catchError, from, of, switchMap } from 'rxjs';

interface NavRequest {
    commands: readonly unknown[];
    extras?: NavigationExtras;
}

/**
 * Fire-and-forget router navigation modeled as effects rather than bare `void this.router.navigate(...)` calls.
 * Each navigation runs through an ngxtension `createEffect`, so its lifecycle is tied to the injector and a
 * rejected/cancelled navigation is logged instead of becoming an unhandled promise - the single place the app's
 * "no floating promises / no void fire-and-forget" rule is satisfied for routing.
 */
@Injectable({ providedIn: 'root' })
export class NavigationService {
    private readonly router = inject(Router);

    /** Navigate by a commands array (+ optional extras), e.g. `nav.go({ commands: ['/game', id] })`. */
    readonly go = createEffect<NavRequest>((req$) => {
        return req$.pipe(
            switchMap(({ commands, extras }) =>
                from(this.router.navigate(commands as unknown[], extras)).pipe(
                    catchError((err: unknown) => {
                        console.error('[navigation] navigate failed', err);

                        return of(false);
                    })
                )
            )
        );
    });
}
